import { useMemo, useState } from 'react';
import { useServers, useGrafanaStack } from '@/hooks/useServers';
import { ContainerGroup, StackState } from '@/api/servers.api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Activity, ExternalLink, Play, Search, Server, Square } from 'lucide-react';
import { apiErrorMessage, cn, formatDateTime, formatRelativeTime } from '@/utils/helpers';

/**
 * Servers (ai-management-assistant#1): the test server's containers by compose project, and the Grafana stack with Up
 * (for 2 hours, 8 hours or until stopped) and Down. The buttons start a pipeline of the infra repository; a stack started
 * for some hours is stopped by the server itself when the time is over.
 */

const STATE_TONE: Record<StackState, string> = {
  up: 'bg-green-100 text-green-800',
  partial: 'bg-amber-100 text-amber-800',
  down: 'bg-slate-100 text-slate-700',
  unknown: 'bg-slate-100 text-slate-500',
};

const STATE_TEXT: Record<StackState, string> = {
  up: 'up',
  partial: 'partly up',
  down: 'down',
  unknown: 'unknown (no container list)',
};

/** "in 3 h 20 min" until a future time */
function timeLeft(at: string): string {
  const minutes = Math.max(0, Math.round((new Date(at).getTime() - Date.now()) / 60000));
  const h = Math.floor(minutes / 60);
  return `in ${h ? `${h} h ` : ''}${minutes % 60} min`;
}

function containerTone(state: string): string {
  if (state === 'running') return 'bg-green-100 text-green-800';
  if (state === 'restarting' || state === 'paused') return 'bg-amber-100 text-amber-800';
  return 'bg-slate-100 text-slate-600';
}

function GrafanaCard() {
  const { data } = useServers();
  const stack = useGrafanaStack();
  const [message, setMessage] = useState<string | null>(null);
  if (!data) return null;
  const g = data.grafana;
  const last = g.lastRequest;

  const press = (action: 'up' | 'down', hours?: number) => {
    setMessage(null);
    stack.mutate(
      { action, hours },
      {
        onSuccess: (outcome) =>
          setMessage(
            outcome.decision === 'executed'
              ? `${action === 'up' ? 'Starting' : 'Stopping'} the stack: the pipeline is running, this page updates by itself.`
              : outcome.decision === 'deduped'
                ? 'Already asked a moment ago.'
                : `Not started: ${outcome.denyReason || outcome.decision}.`
          ),
        onError: (error) => setMessage(apiErrorMessage(error)),
      }
    );
  };

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-4 space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Grafana stack
            <span className={cn('rounded px-2 py-0.5 text-xs font-medium', STATE_TONE[g.state])}>{STATE_TEXT[g.state]}</span>
          </CardTitle>
          <CardDescription className="mt-1">
            Logs, metrics and traces of the test apps. Not needed all the time: start it when you need it.
          </CardDescription>
        </div>
        {(g.state === 'up' || g.state === 'partial') && (
          <a href={g.url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-sm text-primary hover:underline">
            Open Grafana <ExternalLink className="h-4 w-4" />
          </a>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={() => press('up', 2)} disabled={stack.isPending}>
            <Play className="mr-1 h-4 w-4" /> Up for 2 hours
          </Button>
          <Button size="sm" onClick={() => press('up', 8)} disabled={stack.isPending}>
            <Play className="mr-1 h-4 w-4" /> Up for 8 hours
          </Button>
          <Button size="sm" variant="outline" onClick={() => press('up')} disabled={stack.isPending}>
            <Play className="mr-1 h-4 w-4" /> Up until stopped
          </Button>
          <Button size="sm" variant="destructive" onClick={() => press('down')} disabled={stack.isPending || g.state === 'down'}>
            <Square className="mr-1 h-4 w-4" /> Down
          </Button>
        </div>
        {message && <p className="text-sm text-muted-foreground">{message}</p>}

        <div className="space-y-1 text-sm">
          {g.state === 'partial' && <p className="text-amber-700">Not running: {g.missing.join(', ')}</p>}
          {g.running.length > 0 && <p className="text-muted-foreground">Running: {g.running.join(', ')}</p>}
          {g.stopsAt && (
            <p>
              Stops by itself at {formatDateTime(g.stopsAt)} ({timeLeft(g.stopsAt)}).
            </p>
          )}
          {last && (
            <p className="text-muted-foreground">
              Last: {last.action === 'up' ? `up ${last.hours ? `for ${last.hours} h` : 'until stopped'}` : 'down'}
              {last.by ? ` by @${last.by}` : ''}, {formatRelativeTime(last.at)}
              {last.url && (
                <>
                  {' · '}
                  <a href={last.url} target="_blank" rel="noreferrer" className="text-primary hover:underline">
                    pipeline {last.pipelineId}
                  </a>
                </>
              )}
              {last.status ? ` (${last.status})` : ''}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function ContainerTable({ group }: { group: ContainerGroup }) {
  return (
    <div>
      <h3 className="mb-2 flex items-center gap-2 text-sm font-medium">
        {group.project}
        <span className="text-xs font-normal text-muted-foreground">
          {group.running} of {group.containers.length} running
        </span>
      </h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-xs text-muted-foreground">
              <th className="px-2 py-1 font-medium">Container</th>
              <th className="px-2 py-1 font-medium">Image</th>
              <th className="px-2 py-1 font-medium">State</th>
              <th className="px-2 py-1 font-medium">Status</th>
              <th className="px-2 py-1 font-medium">Ports</th>
            </tr>
          </thead>
          <tbody>
            {group.containers.map((c) => (
              <tr key={c.name} className="border-b last:border-0">
                <td className="px-2 py-1 font-mono text-xs">{c.name}</td>
                <td className="max-w-[18rem] truncate px-2 py-1 text-xs text-muted-foreground" title={c.image}>
                  {c.image}
                </td>
                <td className="px-2 py-1">
                  <span className={cn('rounded px-1.5 py-0.5 text-[11px]', containerTone(c.state))}>{c.state}</span>
                </td>
                <td className="px-2 py-1 text-xs">{c.status}</td>
                <td className="px-2 py-1 font-mono text-xs text-muted-foreground">{c.ports.join(', ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Servers() {
  const { data, isLoading, error } = useServers();
  const [query, setQuery] = useState('');
  const [stoppedOnly, setStoppedOnly] = useState(false);

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (data?.containers || [])
      .map((g) => ({
        ...g,
        containers: g.containers.filter(
          (c) =>
            (!stoppedOnly || c.state !== 'running') &&
            (!q || [c.name, c.image, g.project, c.service || ''].some((v) => v.toLowerCase().includes(q)))
        ),
      }))
      .filter((g) => g.containers.length > 0);
  }, [data, query, stoppedOnly]);

  if (isLoading) return <LoadingSpinner className="py-24" />;
  if (error || !data) return <p className="text-sm text-red-600">Could not load the servers: {apiErrorMessage(error)}</p>;

  const total = (data.containers || []).reduce((n, g) => n + g.containers.length, 0);
  const running = (data.containers || []).reduce((n, g) => n + g.running, 0);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold">
          <Server className="h-6 w-6" /> Servers
        </h1>
        <p className="text-sm text-muted-foreground">The test server's containers, refreshed every 30 seconds.</p>
      </div>

      <GrafanaCard />

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
          <div>
            <CardTitle>Containers</CardTitle>
            {data.containers && (
              <CardDescription>
                {running} of {total} running
              </CardDescription>
            )}
          </div>
          {data.containers && (
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-1 text-sm">
                <input type="checkbox" checked={stoppedOnly} onChange={(e) => setStoppedOnly(e.target.checked)} />
                Only not running
              </label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Name, image, project" className="w-56 pl-8" />
              </div>
            </div>
          )}
        </CardHeader>
        <CardContent className="space-y-6">
          {!data.containers ? (
            <p className="text-sm text-muted-foreground">
              The container list does not answer (the "containers" service of the AI manager's compose project). The Grafana
              buttons still work.
            </p>
          ) : groups.length ? (
            groups.map((g) => <ContainerTable key={g.project} group={g} />)
          ) : (
            <p className="py-6 text-center text-sm text-muted-foreground">No containers match</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
